import React from 'react';
import VideoStage from '../VideoStage';
import { normalizeRotation } from '../../videoUpload/rotation';

// The source-footage PICTURE, and the three things that can stand in its place.
// Extracted so the two cards that show a project's source video — the uploaded
// -footage card and the compiled-video card — render the identical preview
// rather than two drifting copies of it.
//
// Player-shaped stand-in for the preview while there is nothing playable to
// show yet (#478). Sized like the <video> it replaces so the real player
// swapping in doesn't shove the page around, and deliberately wordless: the old
// copy read as an error report, when the honest answer is "wait".
//
// Two states, decided by whether the worker has published a percentage:
//   - determinate: the transcode is running and reporting `video.proxyProgress`
//     (a 63s 1080p MJPEG clip took ~111s on the old 2-vCPU worker, long enough
//     that a bar with no numbers on it reads as "stuck").
//   - indeterminate: no percentage exists yet — the pre-probe window, the gap
//     before ffmpeg's first progress line, or a source whose container reports
//     no duration to divide by. Unchanged from #478.
export const PreviewLoading = ({ progressPct = null }) => {
    const determinate = Number.isFinite(progressPct);
    return (
        <div className="pv-preview-loading" data-testid="proxy-pending-loading">
            <div className="pv-preview-loading-inner">
                <div
                    className="pv-preview-loading-track"
                    {...(determinate ? {
                        role: 'progressbar',
                        'aria-valuenow': progressPct,
                        'aria-valuemin': 0,
                        'aria-valuemax': 100,
                    } : {})}
                >
                    <div
                        className={`pv-preview-loading-bar${determinate ? ' pv-preview-loading-bar--determinate' : ''}`}
                        style={determinate ? { width: `${progressPct}%` } : undefined}
                    />
                </div>
                <div className="pv-preview-loading-label">
                    {/* role="status" is an aria-live region, so it wraps ONLY the
                        static sentence: it announces "a preview is being prepared"
                        once. The percentage is deliberately outside it — inside,
                        a screen reader would re-speak the whole placeholder on
                        every update for the length of the transcode, and the
                        progressbar's aria-valuenow already reports that value. */}
                    <span role="status">Preparing preview…</span>
                    {determinate && (
                        <span className="pv-preview-loading-pct" data-testid="proxy-pending-pct">
                            {progressPct}%
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

// video.proxyProgress as something safe to put in a width: a whole 0-100, or
// null when the field is absent/garbage (an old document, or a partial write).
// Anything non-finite must fall back to the indeterminate bar rather than
// render `width: NaN%`.
export const proxyProgressPct = (video) => {
    const raw = video?.proxyProgress;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    return Math.min(100, Math.max(0, Math.round(raw)));
};

// `isPreviewPending` is ProjectView's verdict, not a card's: it covers both the
// declared proxy build and the pre-probe window where the source turned out to
// be undecodable. It lives up there so every playback surface hides together —
// the Footage tab is only the one that explains why.
//
// ProjectView also withholds videoUrl while the verdict holds, so in the real
// app the two props can't disagree. The guard below is belt-and-braces anyway:
// the props arrive independently on exported components, and a caller that
// passes both would otherwise stack the placeholder on top of the very player
// it replaces.
const SourcePreview = ({
    video,
    videoUrl,
    // The project's footageRotation, not a property of the video document —
    // it is a pending instruction to the renderer, applied to the preview by
    // CSS so the user can see what they asked for.
    rotation = 0,
    width = null,
    height = null,
    isPreviewPending = false,
    onPlay,
    onError,
    onRotate = null,
    rotateDisabled = false,
    showRotationNote = true,
}) => {
    // proxyStatus is only ever set for sources browsers can't decode (e.g.
    // Brinno's MJPEG-in-MP4, #473). It decides what stands in for the player.
    const proxyStatus = video?.proxyStatus;
    const angle = normalizeRotation(rotation);
    const playable = !!videoUrl && !isPreviewPending;

    return (
        <>
            {isPreviewPending && <PreviewLoading progressPct={proxyProgressPct(video)} />}
            {proxyStatus === 'failed' && (
                <div className="pv-proxy-note" data-testid="proxy-failed-note">
                    This file&apos;s format can&apos;t be previewed in the
                    browser. Analysis and rendering still work normally.
                </div>
            )}
            {/* Rotation rides IN the player's control bar rather than in a row
                underneath it. Same placement argument as before — the user
                discovers this problem by LOOKING at their footage, so the fix
                belongs in the same eyeful as the symptom — taken one step
                further: the control is now physically on the picture it
                corrects. */}
            {playable && (
                <VideoStage
                    src={videoUrl}
                    rotation={angle}
                    width={width}
                    height={height}
                    onPlay={onPlay}
                    onError={onError}
                    onRotate={onRotate}
                    rotateDisabled={rotateDisabled}
                />
            )}
            {/* The note the row used to carry. It is the one line that stops
                rotation looking expensive: a user who has just waited out a
                200GB upload has no way to know that straightening the picture
                is not another one. */}
            {showRotationNote && onRotate && playable && (
                <p className="pv-rotation-note">
                    Rotation is applied when your video is rendered. Your original
                    files aren&apos;t changed.
                </p>
            )}
        </>
    );
};

export default SourcePreview;
